import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../../components/DashboardLayout";
import AuditsPaywall from "../../../components/account/AuditsPaywall";
import WeaveLoader from "../../../components/common/WeaveLoader";
import { seoService } from "../../../services/seo";
import { getApiErrorMessage } from "../../../utils/axios";
import { sitePath } from "../../../utils/urls";
import { SECTIONS } from "./constants";
import { IssueDrawer, PageDrawer, SuggestionDrawer } from "./Drawers";
import { IssuesPanel } from "./Issues";
import { PagesPanel } from "./Pages";
import { ReportDetail, ReportsList, RunReportMenu } from "./Reports";
import { SuggestionsList } from "./Suggestions";
import { SeoEmptyState } from "./shared";
import { groupSuggestions, formatTemplatePath } from "./utils";

const Seo = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = useSelector((state) => state.project?.currentProject);
  const hasAuditsAccess = Boolean(currentUser?.features?.audits);

  const tab = SECTIONS.find((item) => location.pathname.includes(`/seo/${item.id}/`))?.id || "reports";
  const pathParts = location.pathname.split("/").filter(Boolean);
  const seoIdx = pathParts.indexOf("seo");
  const detailHash = seoIdx >= 0 && pathParts[seoIdx + 2] ? pathParts[seoIdx + 2] : null;
  const reportHash = tab === "reports" ? detailHash : null;
  const pageHash = tab === "pages" ? detailHash : null;
  const issueKey = tab === "issues" ? searchParams.get("issue") : null;
  const suggestionGroup = tab === "suggestions" ? searchParams.get("group") : null;
  const focusCheck = searchParams.get("check");
  const reportFilter = searchParams.get("report") || "";

  const [reports, setReports] = useState([]);
  const [latestReports, setLatestReports] = useState({});
  const [reportsCount, setReportsCount] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsHasNext, setReportsHasNext] = useState(false);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [report, setReport] = useState(null);
  const [pagesData, setPagesData] = useState(null);
  const [issuesData, setIssuesData] = useState(null);
  const [suggestionsPayload, setSuggestionsPayload] = useState({ suggestions: [], actionable: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageFilter, setPageFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [runOpen, setRunOpen] = useState(false);
  const [newType, setNewType] = useState("page_audit");
  const [creating, setCreating] = useState(false);
  const [reportCheckIssue, setReportCheckIssue] = useState(null);

  useEffect(() => {
    if (!hasAuditsAccess || !project?.hash) return;
    if (!location.pathname.includes("/seo/reports/")
      && !location.pathname.includes("/seo/issues/")
      && !location.pathname.includes("/seo/pages/")
      && !location.pathname.includes("/seo/suggestions/")) {
      navigate(sitePath(project, "seo/reports/"), { replace: true });
    }
  }, [hasAuditsAccess, location.pathname, navigate, project]);

  const applyReportsPayload = useCallback((data, { append = false } = {}) => {
    const results = Array.isArray(data) ? data : (data?.results || []);
    setReports((prev) => (append ? [...prev, ...results] : results));
    if (!Array.isArray(data)) {
      setReportsCount(data?.count ?? results.length);
      setReportsPage(data?.page || 1);
      setReportsHasNext(!!data?.has_next);
      if (data?.latest) setLatestReports(data.latest);
    } else {
      setReportsCount(results.length);
      setReportsHasNext(false);
    }
    setReportsLoaded(true);
  }, []);

  const loadReports = useCallback((opts = {}) => {
    if (!project?.hash) return;
    const page = opts.page || 1;
    const type = opts.type !== undefined ? opts.type : typeFilter;
    const append = !!opts.append;
    if (append) setLoadingMore(true);
    else if (!opts.silent) setLoading(true);
    seoService.listReports(
      project.hash,
      { type, page },
      (response) => {
        applyReportsPayload(response.data, { append });
        setLoading(false);
        setLoadingMore(false);
      },
      () => {
        setError("Could not load reports.");
        setLoading(false);
        setLoadingMore(false);
        setReportsLoaded(true);
      },
    );
  }, [project?.hash, typeFilter, applyReportsPayload]);

  const loadReport = useCallback((opts = {}) => {
    if (!project?.hash || !reportHash) {
      setReport(null);
      return;
    }
    if (!opts.silent) setReport(null);
    seoService.getReport(reportHash, project.hash, (response) => {
      setReport(response.data);
    }, () => setError("Could not load report."));
  }, [project?.hash, reportHash]);

  const loadPages = useCallback(() => {
    if (!project?.hash) return;
    seoService.listPages(
      project.hash,
      reportFilter ? { report: reportFilter } : {},
      (response) => setPagesData(response.data),
      () => setError("Could not load pages."),
    );
  }, [project?.hash, reportFilter]);

  const loadIssues = useCallback(() => {
    if (!project?.hash) return;
    seoService.listIssues(
      project.hash,
      reportFilter ? { report: reportFilter } : {},
      (response) => setIssuesData(response.data),
      () => setError("Could not load issues."),
    );
  }, [project?.hash, reportFilter]);

  const loadSuggestions = useCallback(() => {
    if (!project?.hash) return;
    seoService.listSuggestions(
      project.hash,
      reportFilter ? { report: reportFilter } : {},
      (response) => {
        const data = response.data;
        if (Array.isArray(data)) {
          setSuggestionsPayload({ suggestions: data, actionable: true });
        } else {
          setSuggestionsPayload({
            suggestions: data?.suggestions || [],
            actionable: data?.actionable !== false,
          });
        }
      },
      () => setError("Could not load suggestions."),
    );
  }, [project?.hash, reportFilter]);

  useEffect(() => {
    setReportCheckIssue(null);
  }, [reportHash]);

  useEffect(() => {
    if (!hasAuditsAccess || !project?.hash) return;
    setError("");
    loadReports({ page: 1, type: typeFilter });
    if (tab === "reports" && reportHash) loadReport();
    if (tab === "pages") {
      loadPages();
      loadSuggestions();
    }
    if (tab === "issues") loadIssues();
    if (tab === "suggestions") loadSuggestions();
  }, [hasAuditsAccess, project?.hash, tab, reportHash, reportFilter, typeFilter, loadReports, loadReport, loadPages, loadIssues, loadSuggestions]);

  useEffect(() => {
    const active = reports.some((r) => r.status === "queued" || r.status === "running");
    if (!active) return undefined;
    const timer = setInterval(() => {
      loadReports({ page: 1, type: typeFilter, silent: true });
      if (reportHash) loadReport({ silent: true });
    }, 10000);
    return () => clearInterval(timer);
  }, [reports, reportHash, typeFilter, loadReports, loadReport]);

  const groups = useMemo(
    () => groupSuggestions(suggestionsPayload.suggestions || []),
    [suggestionsPayload.suggestions],
  );
  const openGroup = groups.find((g) => g.key === suggestionGroup) || null;
  const openIssue = (issuesData?.issues || []).find((i) => i.key === issueKey) || null;

  const selectedPage = useMemo(() => {
    if (!pageHash || !pagesData?.pages) return null;
    return pagesData.pages.find((p) => p.hash === pageHash) || null;
  }, [pageHash, pagesData]);

  const suggestionsByCheck = useMemo(() => {
    const map = {};
    (suggestionsPayload.suggestions || []).forEach((item) => {
      if (!item.check_code || !item.site_page) return;
      if (selectedPage && item.site_page !== selectedPage.hash) return;
      map[item.check_code] = item;
    });
    return map;
  }, [suggestionsPayload.suggestions, selectedPage]);

  const setReportFilter = (hash) => {
    const next = new URLSearchParams(searchParams);
    if (hash) next.set("report", hash);
    else next.delete("report");
    setSearchParams(next);
  };

  const openPage = (page, check) => {
    const next = new URLSearchParams(searchParams);
    if (check) next.set("check", check);
    else next.delete("check");
    navigate({
      pathname: sitePath(project, `seo/pages/${page.hash}/`),
      search: next.toString() ? `?${next}` : "",
    });
  };

  const closePage = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("check");
    navigate({
      pathname: sitePath(project, "seo/pages/"),
      search: next.toString() ? `?${next}` : "",
    });
  };

  const createReport = (type) => {
    const reportType = type || newType;
    if (!project?.hash || reportType === "indexing") return;
    setCreating(true);
    setRunOpen(false);
    seoService.createReport(project.hash, { type: reportType }, (response) => {
      setCreating(false);
      loadReports({ page: 1, type: typeFilter });
      navigate(sitePath(project, "seo/reports/"));
    }, (err) => {
      setCreating(false);
      setError(getApiErrorMessage(err, "Could not start report."));
    });
  };

  const openFixChange = (hashes) => {
    const open = (Array.isArray(hashes) ? hashes : [])
      .filter(Boolean);
    if (!open.length) return;
    const params = new URLSearchParams({ suggestions: open.join(",") });
    navigate(`${sitePath(project, "changes/new/")}?${params}`);
  };

  const navConfig = SECTIONS.map((item) => {
    const Icon = item.icon;
    return {
      id: item.id,
      label: item.label,
      icon: <Icon className="h-4 w-4" />,
      to: sitePath(project, `seo/${item.id}/`),
      active: tab === item.id,
    };
  });

  const reportScore = report?.scores?.find((score) => score.site_page === searchParams.get("page"));
  const pagesActionable = pagesData?.actionable !== false;
  const issuesActionable = issuesData?.actionable !== false;
  const suggestionsActionable = suggestionsPayload.actionable !== false;
  const showEmpty = reportsLoaded && !loading && reportsCount === 0 && typeFilter === "all";
  const showBootLoader = !reportsLoaded;

  if (!hasAuditsAccess) {
    return (
      <DashboardLayout title="SEO Audits" currentUser={currentUser} navConfig={null}>
        <AuditsPaywall />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="SEO Audits"
      currentUser={currentUser}
      navConfig={showEmpty || showBootLoader ? null : navConfig}
      primaryAction={showEmpty || showBootLoader ? null : (
        <RunReportMenu
          open={runOpen}
          onToggle={() => setRunOpen((v) => !v)}
          creating={creating}
          onCreate={createReport}
        />
      )}
    >
      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {typeof error === "string" ? error : "Something went wrong."}
        </p>
      )}

      {showBootLoader ? (
        <div className="flex items-center justify-center py-24">
          <WeaveLoader size={72} />
        </div>
      ) : showEmpty ? (
        <SeoEmptyState
          selectedType={newType}
          onSelectType={setNewType}
          creating={creating}
          onCreate={createReport}
        />
      ) : (
        <>
      {tab === "reports" && !reportHash && (
        <ReportsList
          reports={reports}
          latest={latestReports}
          loading={loading}
          loadingMore={loadingMore}
          hasNext={reportsHasNext}
          count={reportsCount}
          onLoadMore={() => loadReports({ page: reportsPage + 1, type: typeFilter, append: true })}
          project={project}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          onCreate={createReport}
          creating={creating}
        />
      )}

      {tab === "reports" && reportHash && (
        report?.hash === reportHash ? (
          <ReportDetail
            report={report}
            reports={reports}
            project={project}
            onSwitchReport={(hash) => navigate(sitePath(project, `seo/reports/${hash}/`))}
            onOpenScore={(score) => {
              setReportCheckIssue(null);
              const next = new URLSearchParams(searchParams);
              next.set("page", score.site_page);
              next.delete("check");
              setSearchParams(next);
            }}
            onOpenCheck={(issue) => {
              const next = new URLSearchParams(searchParams);
              next.delete("page");
              next.delete("check");
              setSearchParams(next, { replace: true });
              setReportCheckIssue(issue);
            }}
          />
        ) : (
          <div className="flex items-center justify-center py-16">
            <WeaveLoader size={64} />
          </div>
        )
      )}

      {tab === "issues" && (
        <IssuesPanel
          issuesData={issuesData}
          loading={loading}
          project={project}
          reports={reports}
          reportFilter={reportFilter}
          onReportFilter={setReportFilter}
          onOpenIssue={(issue) => {
            const next = new URLSearchParams(searchParams);
            next.set("issue", issue.key);
            setSearchParams(next);
          }}
        />
      )}

      {tab === "pages" && (
        <PagesPanel
          pagesData={pagesData}
          loading={loading}
          project={project}
          filter={pageFilter}
          onFilter={setPageFilter}
          reports={reports}
          reportFilter={reportFilter}
          onReportFilter={setReportFilter}
          onOpenPage={openPage}
        />
      )}

      {tab === "suggestions" && (
        <SuggestionsList
          groups={groups}
          loading={loading}
          actionable={suggestionsActionable}
          project={project}
          reports={reports}
          reportFilter={reportFilter}
          onReportFilter={setReportFilter}
          onOpen={(group) => {
            const next = new URLSearchParams(searchParams);
            next.set("group", group.key);
            setSearchParams(next);
          }}
        />
      )}
        </>
      )}

      {openIssue && (
        <IssueDrawer
          issue={openIssue}
          project={project}
          actionable={issuesActionable}
          onFix={openFixChange}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("issue");
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {reportCheckIssue && (
        <IssueDrawer
          issue={reportCheckIssue}
          project={project}
          actionable={!!report?.is_latest && report?.status === "succeeded"}
          onFix={openFixChange}
          onClose={() => setReportCheckIssue(null)}
        />
      )}

      {selectedPage && (
        <PageDrawer
          title={formatTemplatePath(selectedPage)}
          previewPath={selectedPage.route || "/"}
          project={project}
          checks={selectedPage.audit?.checks || selectedPage.page_speed?.checks || selectedPage.ux_audit?.checks || []}
          samples={selectedPage.audit?.samples || selectedPage.page_speed?.samples || selectedPage.ux_audit?.samples || []}
          focusCheck={focusCheck}
          actionable={pagesActionable}
          suggestionsByCheck={suggestionsByCheck}
          onFix={openFixChange}
          onClose={closePage}
        />
      )}

      {tab === "reports" && reportScore && (
        <PageDrawer
          title={formatTemplatePath(reportScore.site_page_path)}
          previewPath={reportScore.site_page_route || reportScore.samples?.[0]?.path || "/"}
          project={project}
          checks={reportScore.checks || []}
          samples={reportScore.samples || []}
          focusCheck={focusCheck}
          actionable={!!report?.is_latest}
          suggestionsByCheck={Object.fromEntries(
            (report?.suggestions || [])
              .filter((s) => s.site_page === reportScore.site_page && s.check_code)
              .map((s) => [s.check_code, s]),
          )}
          onFix={openFixChange}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("page");
            next.delete("check");
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {openGroup && (
        <SuggestionDrawer
          group={openGroup}
          project={project}
          actionable={suggestionsActionable}
          onFix={openFixChange}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("group");
            setSearchParams(next, { replace: true });
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default Seo;
